import { get, set } from "lodash";
import {
  IFieldArray,
  IFieldRuntimeObject,
  ITemplateScope,
} from "AngularFormly";
import {
  ContractService,
  vsServiceName as ContractServiceName,
} from "../../../../Contract/shared/ContractService";
import {
  FormViewConfig,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
} from "../../../../records/forms/formView";
import { RecordDataSource } from "../../../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../../records/grids/columns/columnDefinitionService";
import { FilterProvider } from "../../../../records/grids/columns/filters/FilterProvider";
import { OptionsFilter } from "../../../../records/grids/columns/filters/subFilters/OptionsFilter";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../../../records/grids/settings/gridSettingsService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../records/server/serverUtility";
import {
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../../../records/views/recordGridView";
import { ICompileService, IScope } from "angular";
import fieldHelpers from "../../../../ui-formly/fieldHelpers";
import {
  FieldConfigurationHelperService,
  FieldConfigurationHelperWatchers,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../records/forms/formly/fieldConfigurationHelpers";
import { LookupManagementService } from "../../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName as LookupManagementServiceName } from "./lookupManagement";

import { vsObjectName as performanceGuaranteeName } from "./../../../../Contract/shared/lookup/performanceGuaranteeType";
import { vsObjectName as performanceGuaranteePartsName } from "./../../../../Contract/shared/lookup/performanceGuaranteePartsType";
import { vsObjectName as feeStructureName } from "./../../../../Contract/shared/lookup/feeStructureType";
import {
  vsServiceName as ContractInstanceFieldsServiceName,
  ContractInstanceFieldsService,
} from "../../../../Contract/shared/forms/contractInstanceFields";
import { vsObjectName as typeOfAgreementName } from "./../../../../Contract/shared/lookup/typeOfAgreement";
import { vsObjectName as paymentTermName } from "./../../../../Contract/shared/lookup/paymentTerm";
import { vsObjectName as costTypeName } from "./../../../../Contract/shared/lookup/costType";
import { vsObjectName as contractInstanceName } from "../../../../Contract/shared/configs/contractInstance";
import { moneyValueGetterFactory } from "../../../../records/grids/cells/valueGetters";
import { ITemplateOptions } from "AngularFormly";
import {
  ColumnDefinitionSetService,
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../../../records/grids/columns/columnDefinitionSetService";

GamingContractConfig.$inject = [
  FormViewServiceProviderName,
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
];

export const vsObjectName = "gamingContract";
export const defaultSort = "ContractNumber";

export function GamingContractConfig(
  formViewServiceProvider: FormViewServiceProvider,
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  configureForm(formViewServiceProvider);
  configureColumnDefinitionSet(columnDefinitionSetServiceProvider);
  configureGrid(recordGridViewServiceProvider);
}

function configureColumnDefinitionSet(
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsObjectName, [
    ColumnDefinitionServiceName,
    ColumnDefinitionSetServiceName,
    "$compile",
    (
      columnDefinitionService: ColumnDefinitionService,
      columnDefinitionSetService: ColumnDefinitionSetService,
      $compile: ICompileService
    ) => [
      ...columnDefinitionSetService.getColumnDefinitionSet(
        contractInstanceName
      ),
      columnDefinitionService.getStringColDef({
        field: "GamingContractTypeOfAgreementName",
        header: "Type Of Agreement",
      }),
      columnDefinitionService.getStringColDef({
        field: "GamingContractPaymentTermName",
        header: "Payment Term",
        hide: true,
        requiresPermission: "contractSensitiveInfo",
      }),
      columnDefinitionService.getDateColDef({
        field: "DeliveryDate",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "ContractValue",
        header: "Total Value",
        valueGetter: moneyValueGetterFactory(),
        requiresPermission: "contractSensitiveInfo",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "TotalValueThresholdIndicator",
        header: "Total Value Flag",
        requiresPermission: "contractSensitiveInfo",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "SumTotal",
        header: "Sum Line Item Total",
        requiresPermission: "contractSensitiveInfo",
        valueGetter: moneyValueGetterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "SumNetTotal",
        header: "Sum Line Item Net Total",
        requiresPermission: "contractSensitiveInfo",
        valueGetter: moneyValueGetterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "SumDiscount",
        header: "Sum Line Item Discount",
        requiresPermission: "contractSensitiveInfo",
        valueGetter: moneyValueGetterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "OwnedLeased",
        header: "Owned/Leased",
        hide: true,
        filter: new FilterProvider("OwnedLeased", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              options: [
                {
                  display: "Leased",
                  value: "Leased",
                },
                {
                  display: "Owned",
                  value: "Owned",
                },
                {
                  display: "Trial Agreement",
                  value: "TrialAgreement",
                },
                {
                  display: "Lease To Own",
                  value: "LeaseToOwn",
                },
              ],
              queryFieldNames: ["OwnedLeased"],
            })
          )
          .getGridFilterFactory(),
        requiresPermission: "contractSensitiveInfo",
      }),
      columnDefinitionService.getStringColDef({
        field: "GamingContractPerformanceGuaranteeTypeName",
        header: "Performance Guarantee",
        hide: true,
        requiresPermission: "contractSensitiveInfo",
      }),
      columnDefinitionService.getStringColDef({
        field: "PerformanceGuaranteeDetails",
        header: "Performance Guarantee Details",
        requiresPermission: "contractSensitiveInfo",
        hide: true,
      }),
      columnDefinitionService.getDateColDef({
        field: "PerformanceGuaranteeEffectiveDate",
        header: "Performance Guarantee Effective Date",
        requiresPermission: "contractSensitiveInfo",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "GamingContractPerformanceGuaranteeTypePartsName",
        header: "Performance Guarantee Parts",
        hide: true,
        requiresPermission: "contractSensitiveInfo",
      }),
      columnDefinitionService.getStringColDef({
        field: "PerformanceGuaranteePartsDetails",
        header: "Performance Guarantee Parts Details",
        hide: true,
        requiresPermission: "contractSensitiveInfo",
      }),
      columnDefinitionService.getDateColDef({
        field: "PerformanceGuaranteePartsDetails",
        header: "Performance Guarantee Parts Details",
        hide: true,
        requiresPermission: "contractSensitiveInfo",
      }),
      columnDefinitionService.getDateColDef({
        field: "LeaseToOwnDate",
        header: "Lease Period End Date",
        hide: true,
        requiresPermission: "contractSensitiveInfo",
      }),
      columnDefinitionService.getStringColDef({
        field: "LeaseToOwnDetails",
        header: "Lease to Own Details",
        hide: true,
        requiresPermission: "contractSensitiveInfo",
      }),
      columnDefinitionService.getDateColDef({
        field: "EndOfTrialAgreementDate",
        header: "End Of Trial Agreement",
        hide: true,
        requiresPermission: "contractSensitiveInfo",
      }),
      columnDefinitionService.getStringColDef({
        field: "TrialAgreementDetails",
        header: "Trial Agreement Details",
        hide: true,
        requiresPermission: "contractSensitiveInfo",
      }),
    ],
  ]);
}

function configureGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider
) {
  recordGridViewServiceProvider.addGridConfiguration(vsObjectName, [
    ServerUtilityName,
    GridSettingsServiceName,
    (
      serverUtility: ServerUtility,
      gridSettingsService: GridSettingsService
    ) => {
      return {
        gridDataSource: new RecordDataSource(
          serverUtility,
          "/api/contract/gamingContract"
        ),
        defaultSort,
        nameSingular: "Gaming Contract",
        showAddButton: true,
        showAddButtonPermission: ["contractEdit", "contractSensitiveInfo"],
        gridSettings: gridSettingsService.getGridSettings(
          Symbol("contract.type.gamingcontract")
        ),
        columnDefinitionSet: vsObjectName,
      };
    },
  ]);
}

function configureForm(formViewServiceProvider: FormViewServiceProvider) {
  formViewServiceProvider.addFormViewConfig(vsObjectName, [
    ContractServiceName,
    FieldConfigurationHelperServiceName,
    LookupManagementServiceName,
    ContractInstanceFieldsServiceName,
    (
      contractService: ContractService,
      fieldConfigurationHelperService: FieldConfigurationHelperService,
      lookupManagementService: LookupManagementService,
      contractInstanceFieldsService: ContractInstanceFieldsService
    ) => {
      const contractType = contractService.getContractType(vsObjectName)!;
      const performanceGuaranteeDetailsField = fieldHelpers.simpleStringField({
        key: "PerformanceGuaranteeDetails",
        name: "PerformanceGuaranteeDetails",
        label: "Details",
        watcher: [
          FieldConfigurationHelperWatchers.nullWhenNotValue(
            "model.OwnedLeased",
            "Owned"
          ),
        ],
      });

      const performanceGuaranteePartsDetailsField =
        fieldHelpers.simpleStringField({
          key: "PerformanceGuaranteePartsDetails",
          name: "PerformanceGuaranteePartsDetails",
          label: "Details",
          watcher: [
            FieldConfigurationHelperWatchers.nullWhenNotValue(
              "model.OwnedLeased",
              "Owned"
            ),
          ],
        });

      const feeStructureDetailsField = fieldHelpers.simpleStringField({
        key: "FeeStructureDetails",
        name: "FeeStructureDetails",
        label: "Details",
      });
      const TrialAgreementDetailsField = fieldHelpers.simpleStringField({
        key: "TrialAgreementDetails",
        name: "TrialAgreementDetails",
        label: "Details",
        watcher: [
          FieldConfigurationHelperWatchers.nullWhenNotValue(
            "model.OwnedLeased",
            "TrialAgreement"
          ),
        ],
      });
      const costTypeDetailsField = fieldHelpers.simpleStringField({
        key: "Description",
        name: "Description",
        label: "Description",
      });

      const leaseToOwnDetailsField = fieldHelpers.simpleStringField({
        key: "LeaseToOwnDetails",
        name: "LeaseToOwnDetails",
        label: "Lease To Own Details",
        watcher: [
          FieldConfigurationHelperWatchers.nullWhenNotValue(
            "model.OwnedLeased",
            "LeaseToOwn"
          ),
        ],
      });

      const formFields: IFieldArray = [
        ...contractInstanceFieldsService.contractInstanceFields(),
        ...fieldHelpers.columnLayout(
          [
            fieldConfigurationHelperService.lookupField(
              lookupManagementService,
              {
                objectName: typeOfAgreementName,
                key: "GamingContractTypeOfAgreementId",
                required: true,
              }
            ),
            fieldConfigurationHelperService.lookupField(
              lookupManagementService,
              {
                objectName: paymentTermName,
                key: "GamingContractPaymentTermId",
                required: true,
              }
            ),
            fieldHelpers.simpleDateField({
              key: "DeliveryDate",
              label: "Delivery Date",
            }),
          ],
          4
        ),
        fieldHelpers.headerField({
          headerText: "Agreement Information",
          key: "AgreementInformationHeader",
        }),
        ...fieldHelpers.columnLayout(
          [
            {
              key: "OwnedLeased",
              name: "OwnedLeased",
              type: "select",
              templateOptions: {
                label: "Owned/Leased",
                options: [
                  {
                    label: "Leased",
                    value: "Leased",
                  },
                  {
                    label: "Owned",
                    value: "Owned",
                  },
                  {
                    label: "Lease To Own",
                    value: "LeaseToOwn",
                  },
                  {
                    label: "Trial Agreement",
                    value: "TrialAgreement",
                  },
                ],
                required: true,
                labelProp: "label",
                valueProp: "value",
              },
            },
          ],
          4
        ),
        ...fieldHelpers.columnLayout(
          [
            fieldHelpers.simpleDateField({
              key: "PerformanceGuaranteeEffectiveDate",
              label: "Effective Date",
              watcher: [
                FieldConfigurationHelperWatchers.nullWhenNotValue(
                  "model.OwnedLeased",
                  "Owned"
                ),
              ],
            }),
          ],
          4,
          undefined,
          "model.OwnedLeased !== 'Owned'"
        ),
        ...fieldHelpers.columnLayout(
          [
            fieldConfigurationHelperService.lookupField(
              lookupManagementService,
              {
                objectName: performanceGuaranteeName,
                key: "GamingContractPerformanceGuaranteeTypeId",
                watcher: [
                  FieldConfigurationHelperWatchers.nullWhenNotValue(
                    "model.OwnedLeased",
                    "'Owned'",
                    ["GamingContractPerformanceGuaranteeType"]
                  ),
                ],
              }
            ),
            performanceGuaranteeDetailsField,
          ],
          4,
          undefined,
          "model.OwnedLeased !== 'Owned'"
        ),
        ...fieldHelpers.columnLayout(
          [
            fieldConfigurationHelperService.lookupField(
              lookupManagementService,
              {
                objectName: performanceGuaranteePartsName,
                key: "GamingContractPerformanceGuaranteeTypePartsId",
                required: false,
                watcher: [
                  FieldConfigurationHelperWatchers.nullWhenNotValue(
                    "model.OwnedLeased",
                    "'Owned'",
                    ["GamingContractPerformanceGuaranteeTypeParts"]
                  ),
                ],
              }
            ),
            performanceGuaranteePartsDetailsField,
          ],
          4,
          undefined,
          "model.OwnedLeased !== 'Owned'"
        ),
        fieldConfigurationHelperService.repeaterField({
          key: "FeeStructures",
          name: "FeeStructures",
          label: "FeeStructures",
          allowAdd: true,
          allowAddBottom: true,
          displayNameSingular: "Fee Structure",
          displayNamePlural: "Fee Structures",
          repeaterFields: [
            ...fieldHelpers.columnLayout(
              [
                fieldConfigurationHelperService.lookupField(
                  lookupManagementService,
                  {
                    key: "GamingContractFeeStructureTypeId",
                    objectName: feeStructureName,
                    required: false,
                  }
                ),
                feeStructureDetailsField,
              ],
              4
            ),
          ],
          showHeader: true,
          hideExpression: (viewValue: any, _, scope) =>
            scope.model?.OwnedLeased !== "Leased",
          watcher: {
            expression: (field, scope) => scope.model?.OwnedLeased,
            listener: (
              field: IFieldRuntimeObject,
              newValue: any,
              _: any,
              scope: ITemplateScope
            ) => {
              const { model } = scope;
              if (newValue !== "Leased") {
                if (field.key) {
                  set(model ?? {}, field.key, []);
                }
              }
            },
          },
        }),
        ...fieldHelpers.columnLayout(
          [
            fieldHelpers.simpleDateField({
              key: "EndOfTrialAgreementDate",
              label: "End Of Trial Agreement",
              watcher: [
                FieldConfigurationHelperWatchers.nullWhenNotValue(
                  "model.OwnedLeased",
                  "'TrialAgreement'"
                ),
              ],
            }),
            TrialAgreementDetailsField,
          ],
          4,
          undefined,
          "model.OwnedLeased !== 'TrialAgreement'"
        ),
        ...fieldHelpers.columnLayout(
          [
            fieldHelpers.simpleDateField({
              key: "LeaseToOwnDate",
              label: "Lease Period End Date",
              watcher: [
                FieldConfigurationHelperWatchers.nullWhenNotValue(
                  "model.OwnedLeased",
                  "'LeaseToOwn'"
                ),
              ],
            }),
            leaseToOwnDetailsField,
          ],
          4,
          undefined,
          "model.OwnedLeased !== 'LeaseToOwn'"
        ),

        fieldConfigurationHelperService.repeaterField({
          key: "LineItems",
          name: "LineItems",
          label: "LineItems",
          allowAdd: true,
          allowAddBottom: true,
          displayNameSingular: "Cost",
          displayNamePlural: "Costs",
          repeaterFields: [
            ...fieldHelpers.columnLayout(
              [
                fieldConfigurationHelperService.lookupField(
                  lookupManagementService,
                  {
                    objectName: costTypeName,
                    key: "GamingContractCostTypeId",
                    required: true,
                  }
                ),
                costTypeDetailsField,
              ],
              4
            ),
            ...fieldHelpers.columnLayout(
              [
                fieldHelpers.simpleNumberField({
                  key: "Quantity",
                  name: "Quantity",
                  label: "Quantity",
                  min: 0,
                  step: 1,
                }),
                fieldHelpers.simpleCurrencyField({
                  key: "UnitPrice",
                  name: "UnitPrice",
                  label: "Unit Price",
                  min: 0,
                  step: 0.01,
                }),
                fieldHelpers.simpleCurrencyField({
                  key: "Total",
                  name: "Total",
                  label: "Total",
                  readonly: true,
                  watcher: {
                    expression: (field, scope) => getLineItemTotal(scope.model),
                    listener: setFieldValue,
                    type: "$watchCollection",
                  },
                  model: {},
                }),
                fieldHelpers.simpleCurrencyField({
                  key: "UnitDiscount",
                  name: "UnitDiscount",
                  label: "Unit Discount",
                  min: 0,
                  step: 0.01,
                }),
                fieldHelpers.simpleCurrencyField({
                  key: "Discount",
                  name: "Discount",
                  label: "Discount",
                  readonly: true,
                  watcher: {
                    expression: (field, scope) =>
                      getLineItemDiscount(scope.model),
                    listener: setFieldValue,
                  },
                  model: {},
                }),
                fieldHelpers.simpleCurrencyField({
                  key: "NetTotal",
                  name: "NetTotal",
                  label: "Net Total",
                  readonly: true,
                  watcher: {
                    expression: (field, scope) => getLineItemNet(scope.model),
                    listener: setFieldValue,
                  },
                  model: {},
                }),

                {
                  key: "copyDelete",
                  type: "recordRepeaterCopyDelete",
                  templateOptions: {
                    label: " ",
                    allowMove: true,
                  },
                },
              ],
              4
            ),
          ],
          showHeader: true,
        }),
        ...fieldHelpers.columnLayout(
          [
            fieldHelpers.simpleCurrencyField({
              key: "CalculatedContractValue",
              label: "Calculated Total Value",
              watcher: {
                expression: (field, scope) => getContractTotal(scope.model),
                listener: setFieldValue,
              },
              readonly: true,
              model: {},
            }),
            {
              key: "ContractValue",
              type: "input",
              templateOptions: {
                type: "number",
                label: "Total Value",
                step: 0.01,
                min: 0.0,
                addonRight: {
                  classAddon: "btn-secondary",
                  class: "fa fa-refresh",
                  onClick(
                    options: ITemplateOptions,
                    model: any,
                    scope: IScope
                  ) {
                    model.ContractValue = getContractTotal(model);
                  },
                },
                addonLeft: { class: "fa fa-usd" },
              },
            },
          ],
          4
        ),
        ...contractInstanceFieldsService.contractInstanceContactField(),
        ...contractInstanceFieldsService.contractInstanceFileFields(),
      ];

      costTypeDetailsField.className =
        feeStructureDetailsField.className =
        performanceGuaranteeDetailsField.className =
        performanceGuaranteePartsDetailsField.className =
        TrialAgreementDetailsField.className =
        leaseToOwnDetailsField.className =
          "col-xs-9";

      const config: FormViewConfig = {
        fields: formFields,
        name: vsObjectName,
        title: (model) =>
          model.Id
            ? `Edit ${contractType.name}`
            : `Create ${contractType.name}`,
        submitForm: (args) => {
          return contractType
            .submitContract(<any>args.model, args.formViewController)
            .then((result) => {
              contractType.infoNavigate(result.ContractInstanceId);
            });
        },
      };

      return config;
    },
  ]);
}

function setFieldValue(field: IFieldRuntimeObject, value: number) {
  set(field.model, <string>field.key, toTwoDecimalPlaces(value));
}

function getLineItemTotal(lineItem: any) {
  return (lineItem?.Quantity ?? 0) * (lineItem?.UnitPrice ?? 0);
}

function getLineItemDiscount(lineItem: any) {
  return (lineItem?.Quantity ?? 0) * (lineItem?.UnitDiscount ?? 0);
}

function getLineItemNet(lineItem: any) {
  return getLineItemTotal(lineItem) - getLineItemDiscount(lineItem);
}

function getContractTotal(contract: any) {
  const lineItems: any[] = (contract ?? {}).LineItems ?? [];
  const total = lineItems.reduce((sum, item) => sum + getLineItemNet(item), 0);
  return toTwoDecimalPlaces(total);
}

function toTwoDecimalPlaces(num: number): number {
  return Math.round(num * 100) / 100;
}
